import clsx from 'clsx';
import {
    User,
    Gear,
    Stack,
    SquaresFour,
    Users,
    CreditCard,
    ChartBar,
    ClockCountdown,
    Desktop,
    DownloadSimple,
    ListChecks,
    ShieldCheck
} from '@phosphor-icons/react';
import { useTranslation } from '@client-app/i18n';
import React, {
    useCallback,
    useEffect,
    useState,
    useRef
} from 'react';
import { createPortal } from 'react-dom';
import {
    useLocation,
    useNavigate
} from 'react-router-dom';

import Icon from '@common/components/Icon';
import Logo from '@common/components/Logo';
import Tooltip from '@common/components/Tooltip';
import styles from './Sidebar.module.scss';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import downloadPageImg from '@/assets/images/Download_page_img.png';
import { useAdminContext } from '@/contexts/AdminContext';
import { Permission, useAuth } from '@/contexts/AuthContext';
import { formatPlanDate, formatTrialExpirationText } from '@/contexts/AuthContext/planTextUtils';
import { isExpiredWorkspaceTrialSubscription } from '@/contexts/AuthContext/workspaceSubscriptionUtils';
import { canViewCloudAgentRunAudit } from '@/components/AuditLog/cloudAgentRunAuditAccess';

export interface SidebarMenuItem {
    name: string;
    path?: string;
    onClick?: () => void;
    icon: React.ReactNode;
    end?: boolean;
}

interface SidebarProps {
    menuItems?: SidebarMenuItem[];
    bottomItems?: SidebarMenuItem[];
    forceCollapsed?: boolean;
}

type SidebarHoverCard = 'trial' | 'download';

const DOWNLOAD_HOVER_CARD_WIDTH = 300;
const DOWNLOAD_HOVER_CARD_HEIGHT = 342;

const UtilityButton = ({
    icon,
    label,
    collapsed,
}: {
    icon: React.ReactNode;
    label: string;
    collapsed: boolean;
}) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        className={clsx(
            "flex w-full items-center rounded-lg border border-[#1A162F]/10 bg-[#1A162F]/5 text-[#1A162F] transition-colors hover:bg-[#1A162F]/10",
            collapsed ? "justify-center p-2" : "gap-2 px-3 py-2",
        )}
    >
        <span className="shrink-0">{icon}</span>
        {!collapsed ? (
            <span className="truncate text-xs font-medium">{label}</span>
        ) : null}
    </button>
);

const SidebarItem = ({
    icon: IconNode, label, active, onClick, onMouseEnter, onFocus, collapsed, rightSlot, interactive = true
}: any) => {
    const itemContent = (
        <div
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onFocus={onFocus}
            aria-label={label}
            className={clsx(
                styles.sidebarItem,
                collapsed && styles.collapsed,
                active && styles.active,
                !interactive && styles.nonInteractive,
                "group"
            )}
        >
            {typeof IconNode === 'string' ? (
                <img src={IconNode} alt={label} />
            ) : (
                IconNode
            )}
            {!collapsed && <span>{label}</span>}
            {!collapsed && rightSlot}
        </div>
    );

    if (!collapsed) return itemContent;

    return (
        <Tooltip
            content={label}
            side="right"
            sideOffset={10}
            align="center"
            showArrow={false}
        >
            {itemContent}
        </Tooltip>
    );
};

import UserMenu from '../UserMenu/UserMenu';

const Sidebar: React.FC<SidebarProps> = ({forceCollapsed}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, locale } = useTranslation();
    const {
        currentWorkspace, integrations, permissions, userBilling
    } = useAuth();
    const { modal } = useAdminContext();

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const [activeHoverCard, setActiveHoverCard] = useState<SidebarHoverCard | null>(null);
    const [hoverCardPosition, setHoverCardPosition] = useState<{
        top?: number;
        bottom?: number;
        left: number;
        width: number;
    }>({ left: 0, width: 0 });
    const sidebarRef = useRef<HTMLDivElement>(null);
    const trialItemRef = useRef<HTMLDivElement>(null);
    const downloadItemRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const refreshIntegrations = integrations.refresh;
    const preloadIntegrations = useCallback(() => {
        void refreshIntegrations(false).catch(() => {});
    }, [refreshIntegrations]);

    const handleHoverCardEnter = (
        card: SidebarHoverCard,
        anchorRef: React.RefObject<HTMLDivElement>,
    ) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            if (isSidebarCollapsed) {
                let top = rect.top;
                const popoverHeight = card === 'download' ? DOWNLOAD_HOVER_CARD_HEIGHT : 116;
                const padding = 16;
                if (top + popoverHeight > window.innerHeight - padding) {
                    top = Math.max(padding, window.innerHeight - popoverHeight - padding);
                }
                const left = rect.right + 8;
                setHoverCardPosition({
                    top: top,
                    left,
                    width: card === 'download'
                        ? Math.min(DOWNLOAD_HOVER_CARD_WIDTH, window.innerWidth - left - padding)
                        : 240
                });
            } else {
                const sidebarRect = sidebarRef.current?.getBoundingClientRect();
                const horizontalInset = 12;
                const left = sidebarRect ? sidebarRect.left + horizontalInset : Math.max(12, rect.right - 240);
                const defaultWidth = sidebarRect ? Math.max(220, sidebarRect.width - horizontalInset * 2) : 240;
                setHoverCardPosition({
                    bottom: window.innerHeight - rect.top + 8,
                    left,
                    width: card === 'download'
                        ? Math.min(DOWNLOAD_HOVER_CARD_WIDTH, window.innerWidth - left - horizontalInset)
                        : defaultWidth
                });
            }
        }
        setActiveHoverCard(card);
    };

    const handleHoverCardLeave = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setActiveHoverCard(null);
        }, 150);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (forceCollapsed) setIsSidebarCollapsed(true);
    }, [forceCollapsed]);

    const isPulseActive = location.pathname.startsWith('/pulse');
    const canManageSubscription = permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]);
    const trialEndsAt = currentWorkspace?.subscription?.trial_end_at
        || userBilling.userPlan?.trialEndAt
        || null;
    const trialEndsAtTime = trialEndsAt ? new Date(trialEndsAt).getTime() : null;
    const isExpiredWorkspaceTrial = isExpiredWorkspaceTrialSubscription(currentWorkspace?.subscription);
    const normalizedPlanButtonText = String(userBilling.planText.buttonText || '').trim().toLowerCase();
    const normalizedStartSubscriptionText = t('website.planText.startSubscription').trim().toLowerCase();
    const billingPlanIndicatesTrial = userBilling.planText.status === 'trial'
        || (userBilling.planText.status === 'expired' && normalizedPlanButtonText === normalizedStartSubscriptionText);
    const shouldUseWorkspaceTrialFallback = userBilling.planText.status === 'none';
    const isCurrentWorkspaceTrial = Boolean(
        billingPlanIndicatesTrial
        || (
            shouldUseWorkspaceTrialFallback
            && (
                userBilling.isTrial
                || isExpiredWorkspaceTrial
                || currentWorkspace?.subscription?.plan_badge === 'Trial'
                || (trialEndsAtTime && Number.isFinite(trialEndsAtTime) && trialEndsAtTime > Date.now())
            )
        )
    );
    const shouldUseBillingPlanText = billingPlanIndicatesTrial && Boolean(userBilling.planText.expirationText);
    const freeTrialMessage = shouldUseBillingPlanText
        ? userBilling.planText.expirationText
        : (
            shouldUseWorkspaceTrialFallback && isExpiredWorkspaceTrial && trialEndsAt
            ? t('website.planText.freeTrialExpiredOn', { date: formatPlanDate(trialEndsAt, locale) })
            : formatTrialExpirationText(trialEndsAt, new Date(), locale, t)
        );
    const showFreeTrialCard = Boolean(
        !isSidebarCollapsed
        && canManageSubscription
        && isCurrentWorkspaceTrial
        && freeTrialMessage
    );
    const showTrialEntry = Boolean(
        canManageSubscription
        && isCurrentWorkspaceTrial
        && freeTrialMessage
    );

    const openPricing = () => {
        setActiveHoverCard(null);
        modal.openModal('pricing');
    };

    const downloadsTrigger = (
        <div
            ref={downloadItemRef}
            onMouseEnter={() => handleHoverCardEnter('download', downloadItemRef)}
            onMouseLeave={handleHoverCardLeave}
            className="relative"
        >
            <button
                type="button"
                aria-label={t('website.settingsModal.downloads')}
                title={t('website.settingsModal.downloads')}
                className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[#4D4865] transition-colors hover:bg-black/[0.045] hover:text-[#1A162F]"
            >
                <Desktop size={18} weight="regular" />
            </button>
        </div>
    );

    const collapsedTrialTrigger = showTrialEntry ? (
        <div
            ref={trialItemRef}
            onMouseEnter={() => handleHoverCardEnter('trial', trialItemRef)}
            onMouseLeave={handleHoverCardLeave}
            className="relative"
        >
            <button
                type="button"
                aria-label={freeTrialMessage}
                title={freeTrialMessage}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-[#4D4865] transition-colors hover:bg-white hover:text-[#1A162F]"
                onClick={openPricing}
            >
                <ClockCountdown size={19} weight="regular" />
            </button>
        </div>
    ) : null;

    const collapsedDownloadsTrigger = (
        <div
            ref={downloadItemRef}
            onMouseEnter={() => handleHoverCardEnter('download', downloadItemRef)}
            onMouseLeave={handleHoverCardLeave}
            className="relative"
        >
            <button
                type="button"
                aria-label={t('website.settingsModal.downloads')}
                title={t('website.settingsModal.downloads')}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-[#4D4865] transition-colors hover:bg-white hover:text-[#1A162F]"
            >
                <Desktop size={19} weight="regular" />
            </button>
        </div>
    );

    return (
        <div ref={sidebarRef} className={clsx(styles.sidebar, isSidebarCollapsed && styles.collapsed)}>
            <div className={styles.logoSection}>
                {!isSidebarCollapsed && (
                    <div
                        style={{
                            display: 'flex',
                            height: 40,
                            paddingLeft: 16,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Logo size="24px" hideText={false} />
                    </div>
                )}
                <button
                    className={styles.toggleButton}
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                >
                    <Icon
                        tips={!isSidebarCollapsed ? t('sidebar.collapse') : t('sidebar.expand')}
                        side="right"
                        name={'collapse'}
                    />
                </button>
            </div>

            <div className="mb-1.5 shrink-0 px-2.5 py-1.5">
                <WorkspaceSwitcher
                    collapsed={isSidebarCollapsed}
                    onOpenSettingsTab={(tab) => {
                        navigate(`/${tab}`);
                    }}
                />
            </div>

            <div className={styles.scrollArea}>


                <div className={styles.categoryHeader}>{t('website.settingsModal.account')}</div>
                <SidebarItem
                    icon={<User size={20} weight="regular" />}
                    label={t('sidebar.profile')}
                    active={location.pathname === '/profile'}
                    onClick={() => navigate('/profile')}
                    collapsed={isSidebarCollapsed}
                />
                <SidebarItem
                    icon={<Gear size={20} weight="regular" />}
                    label={t('website.settingsModal.general')}
                    active={location.pathname === '/general'}
                    onClick={() => navigate('/general')}
                    collapsed={isSidebarCollapsed}
                />

                <div className={styles.categoryHeader}>{t('website.settingsModal.workspaceRecord')}</div>
                <SidebarItem
                    icon={<ListChecks size={20} weight="regular" />}
                    label={t('website.settingsModal.valueLedger')}
                    active={location.pathname === '/value-ledger'}
                    onClick={() => navigate('/value-ledger')}
                    collapsed={isSidebarCollapsed}
                />
                {!permissions.isLoading && canViewCloudAgentRunAudit(permissions) ? (
                    <SidebarItem
                        icon={<ShieldCheck size={20} weight="regular" />}
                        label={t('website.settingsModal.audit')}
                        active={location.pathname === '/audit-log'}
                        onClick={() => navigate('/audit-log')}
                        collapsed={isSidebarCollapsed}
                    />
                ) : null}
                <div className={styles.categoryHeader}>{t('website.settingsModal.workspace')}</div>
                <SidebarItem
                    icon={<Stack size={20} weight="regular" />}
                    label={t('website.settingsModal.workspaceSettings')}
                    active={location.pathname === '/workspace-settings'}
                    onClick={() => navigate('/workspace-settings')}
                    collapsed={isSidebarCollapsed}
                />
                <SidebarItem
                    icon={<SquaresFour size={20} weight="regular" />}
                    label={t('website.settingsModal.integrations')}
                    active={location.pathname === '/integrations'}
                    onClick={() => {
                        preloadIntegrations();
                        navigate('/integrations');
                    }}
                    onMouseEnter={preloadIntegrations}
                    onFocus={preloadIntegrations}
                    collapsed={isSidebarCollapsed}
                />
                <SidebarItem
                    icon={<Users size={20} weight="regular" />}
                    label={t('website.settingsModal.users')}
                    active={location.pathname === '/members'}
                    onClick={() => navigate('/members')}
                    collapsed={isSidebarCollapsed}
                />
                <SidebarItem
                    icon={<CreditCard size={20} weight="regular" />}
                    label={t('website.settingsModal.billingAndPlans')}
                    active={location.pathname === '/billing'}
                    onClick={() => navigate('/billing')}
                    collapsed={isSidebarCollapsed}
                />
                <SidebarItem
                    icon={<ChartBar size={20} weight="regular" />}
                    label={t('website.settingsModal.usage')}
                    active={location.pathname === '/usage'}
                    onClick={() => navigate('/usage')}
                    collapsed={isSidebarCollapsed}
                />
            </div>

            {showFreeTrialCard ? (
                <div className="shrink-0 px-3 pb-3">
                    <div className="rounded-[16px] bg-[#071F22] p-3 shadow-[0_10px_28px_rgba(7,31,34,0.18)]">
                        <p className="text-[13px] font-medium leading-5 text-white/80">
                            {freeTrialMessage}
                        </p>
                        <button
                            type="button"
                            onClick={openPricing}
                            className="mt-3 flex h-9 w-full items-center justify-center rounded-[10px] bg-white px-3 text-[13px] font-semibold text-[#1A162F] shadow-sm transition-colors hover:bg-[#F4F5F6]"
                        >
                            {t('website.planText.startSubscription')}
                        </button>
                    </div>
                </div>
            ) : null}

            {isSidebarCollapsed ? (
                <div className="shrink-0 px-2 pb-2">
                    <div className="flex flex-col items-center gap-1.5">
                        {collapsedTrialTrigger}
                        {collapsedDownloadsTrigger}
                    </div>
                </div>
            ) : null}
            
            <UserMenu
                collapsed={isSidebarCollapsed}
                showSettings={false}
                hideEmail
                rightSlot={isSidebarCollapsed ? undefined : downloadsTrigger}
                onOpenSettings={() => navigate('/profile')}
            />

            {activeHoverCard && createPortal(
                <div
                    onMouseEnter={() => {
                        if (timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                            timeoutRef.current = null;
                        }
                    }}
                    onMouseLeave={handleHoverCardLeave}
                    className={clsx(
                        "fixed overflow-hidden z-[99999] animate-in fade-in duration-200",
                        activeHoverCard === 'trial'
                            ? "rounded-[16px] bg-[#071F22] p-3 shadow-[0_10px_28px_rgba(7,31,34,0.18)]"
                            : "rounded-[18px] border border-[#E8EBF3] bg-white p-4 shadow-[0_12px_28px_rgba(26,22,47,0.08)]"
                    )}
                    style={{
                        top: hoverCardPosition.top !== undefined ? `${hoverCardPosition.top}px` : undefined,
                        bottom: hoverCardPosition.bottom !== undefined ? `${hoverCardPosition.bottom}px` : undefined,
                        left: `${hoverCardPosition.left}px`,
                        width: `${hoverCardPosition.width}px`,
                    }}
                >
                    {activeHoverCard === 'trial' ? (
                        <div className="space-y-3">
                            <p className="text-left text-[13px] font-medium leading-5 text-white/80">
                                {freeTrialMessage}
                            </p>
                            <button
                                type="button"
                                onClick={openPricing}
                                className="inline-flex h-9 w-full items-center justify-center rounded-[10px] bg-white px-3 text-[13px] font-semibold text-[#1A162F] shadow-sm transition-colors hover:bg-[#F4F5F6]"
                            >
                                {t('website.planText.startSubscription')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-[#F8F9FC] border border-[#ECEFF5]">
                                <img
                                    src={downloadPageImg}
                                    alt="Bustly Desktop Preview"
                                    className="h-full w-full object-contain object-top"
                                />
                            </div>
                            <div className="mt-3 space-y-3">
                                <p className="px-1 text-left text-[12px] leading-relaxed text-[#666F8D]">
                                    {t('website.downloads.downloadDescriptionShort')}
                                </p>
                                <button
                                    onClick={() => window.open('https://www.bustly.shop/download', '_blank')}
                                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#1A162F] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[#231D3E]"
                                >
                                    <DownloadSimple size={16} weight="bold" />
                                    <span>{t('website.downloads.downloadClient')}</span>
                                </button>
                            </div>
                        </>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default Sidebar;
